import { useEffect } from "react"
import { preloadViewerAssets } from "components/ui/customWebviewer/core/viewerAssetPreload"

// How long to wait for an idle window before warming up anyway. Long enough for the app's own
// first paint and initial API calls to finish, short enough that a user who heads straight for a
// quotation still benefits.
const IDLE_TIMEOUT_MS = 3000

type TIdleWindow = Window & {
	requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number
	cancelIdleCallback?: (handle: number) => void
}

/**
 * Warm Apryse's shared static assets into the HTTP cache once the app is up, so the first
 * quotation editor open doesn't have to download ~9 MB of bundles, wasm and fonts after the click.
 *
 * Gated on being signed in: the login screen can't reach a viewer, and an anonymous visitor
 * should not pay for a speculative download.
 */
export const useViewerAssetPreload = (isEnabled: boolean) => {
	useEffect(() => {
		if (!isEnabled) return

		const idleWindow = window as TIdleWindow
		// Safari has no requestIdleCallback; a plain timeout is a good enough stand-in, since the
		// work is already deferred and best-effort either way.
		if (typeof idleWindow.requestIdleCallback !== "function") {
			const timer = window.setTimeout(() => void preloadViewerAssets(), IDLE_TIMEOUT_MS)
			return () => window.clearTimeout(timer)
		}

		const handle = idleWindow.requestIdleCallback(() => void preloadViewerAssets(), { timeout: IDLE_TIMEOUT_MS })
		return () => idleWindow.cancelIdleCallback?.(handle)
	}, [isEnabled])
}
